import React, { useState, useEffect, useRef } from 'react';
import { Tabs, Tab, Button } from '@mui/material';
import { Helmet } from "react-helmet";
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import Loading from '../Loading';
import RunWorkflowModal from '../RunWorkflowModal/index.js';
import PatientTable from '../PatientTable/index.js';
import VariantsTable from './VariantsTable';
import SessionConstants from '../../config/SessionConstants.js';
import APIUtils from '../../utils/APIUtils';
import BreadCrumbs from '../BreadCrumbs';
import TabPanel from '../../uiComponents/MuiTabs/MuiTabPanel.js';
import muiTabsStyles from '../../uiComponents/MuiTabs/MuiTabsStyles.js';
import { loadViewState, saveViewState, VIEW_SCOPE } from '../../utils/ProgramSummaryViewState.js';
import './index.css';

// How often to silently refresh program data while the page sits open. Variants
// are live; patients are server-cached ~5 min, so faster patient polls just hit
// the cache (harmless), which keeps a single cadence simple.
const PROGRAM_REFRESH_INTERVAL_MS = 60000;

const ProgramSummary = (props) => {
  const classes = muiTabsStyles();

  const [breadCrumbs, setBreadCrumbs] = useState([]);
  const [patientData, setPatientData] = useState([]);
  const [variantData, setVariantData] = useState([]);
  const [runWorkflowModalIsOpen, setRunWorkflowModalIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [programId, setProgramId] = useState(null);
  const [sortTableByDateDesc, setSortTableByDateDesc] = useState(false);
  // Restore the tab the user last viewed before drilling in; defaults to Patients.
  const [selectedTab, setSelectedTab] = useState(() => {
    const saved = loadViewState(VIEW_SCOPE.programSummary, sessionStorage.getItem(SessionConstants.PROGRAM_ID));
    return typeof saved.selectedTab === 'number' ? saved.selectedTab : 0;
  });

  // Signatures of the last applied payloads; poll refreshes skip setState when
  // the incoming data is unchanged so the tables don't needlessly re-render.
  const patientSignatureRef = useRef(null);
  const variantSignatureRef = useRef(null);
  // Monotonic request ids; only the newest issued fetch may apply its response,
  // so overlapping refreshes (focus + visibility + interval) can't apply
  // out-of-order/stale data.
  const patientRequestIdRef = useRef(0);
  const variantRequestIdRef = useRef(0);

  useEffect(() => {
    console.log('---ProgramSummary.componentDidMount()');
    const programId = sessionStorage.getItem(SessionConstants.PROGRAM_ID);

    setProgramId(programId);
    setBreadCrumbs([{ name: 'ProgramSummary' }]);
    getProgramData(programId);
  }, []);

  const fetchPatients = async (programId) => {
    const requestId = ++patientRequestIdRef.current;
    try {
      const patients = await APIUtils.getPatientsByProgramId(programId);
      if (requestId !== patientRequestIdRef.current) return; // superseded
      if (patients) {
        const signature = JSON.stringify(patients);
        if (signature !== patientSignatureRef.current) {
          patientSignatureRef.current = signature;
          setPatientData(patients);
        }
      }
    } catch (error) {
      console.error('Error getting patients');
    }
  };

  const fetchVariants = async (programId) => {
    const requestId = ++variantRequestIdRef.current;
    try {
      const variants = await APIUtils.getVariantsByProgramId(programId);
      if (requestId !== variantRequestIdRef.current) return; // superseded
      if (variants) {
        const signature = JSON.stringify(variants);
        if (signature !== variantSignatureRef.current) {
          variantSignatureRef.current = signature;
          setVariantData(variants);
        }
      }
    } catch (error) {
      console.error('Error getting variants');
    }
  };

  const getProgramData = async (programId) => {
    console.log('ProgramSummary.getProgramData()');
    await Promise.all([fetchPatients(programId), fetchVariants(programId)]);
    setLoading(false);
  };

  useEffect(() => {
    if (!programId) return undefined;

    // Silently refetch so changes made by other users appear without a manual
    // reload: on a poll interval and when the tab regains focus/visibility.
    // Neither fetch toggles the spinner, so updates apply in place without
    // flicker. Patient polls within the 5-min cache window are harmless hits.
    const refreshAll = () => {
      fetchVariants(programId);
      fetchPatients(programId);
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshAll();
    };

    window.addEventListener('focus', refreshAll);
    document.addEventListener('visibilitychange', handleVisibility);
    const pollId = setInterval(refreshAll, PROGRAM_REFRESH_INTERVAL_MS);

    return () => {
      window.removeEventListener('focus', refreshAll);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(pollId);
    };
  }, [programId]);

  const openRunWorkflowModal = () => {
    setRunWorkflowModalIsOpen(true);
  };

  const closeRunWorkflowModal = (response, feedback) => {
    const programId = sessionStorage.getItem(SessionConstants.PROGRAM_ID);
    if (feedback) {
      setRunWorkflowModalIsOpen(false);
      setSortTableByDateDesc(true);
    } else {
      setRunWorkflowModalIsOpen(false);
      setSortTableByDateDesc(false);
      getProgramData(programId);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    saveViewState(VIEW_SCOPE.programSummary, sessionStorage.getItem(SessionConstants.PROGRAM_ID), { selectedTab: newValue });
  };

  // Patients is the default tab, so gate the spinner only on the program data.
  // The variants table preloads (hidden) so its first open has no spinner.
  const contentReady = !loading;

  return (
    <div>
      <Loading isLoading={!contentReady} />
      <Helmet>
        <title>ED3N - Program Summary</title>
      </Helmet>
      <BreadCrumbs breadCrumbs={breadCrumbs} />
      <h1 className="pageTitle">Program Summary</h1>
      {!contentReady && <div className="noDataLarge"></div>}
      {!loading && (
        <div style={{ display: contentReady ? 'block' : 'none' }}>
          <div className="button-bar">
            <Button
              id="runWorkflowButton"
              aria-label="run workflow"
              onClick={openRunWorkflowModal}
              startIcon={<CloudUploadIcon className='startIconLarger' />}
              variant="contained"
              color="primary"
            >
              Run Workflow
            </Button>
          </div>
          <div>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              centered
              className={classes.tabs} // Apply custom styles to Tabs container
            >
              <Tab label="Patients for Review" id="tab-0" aria-controls="tabpanel-0" className={classes.tab} />
              <Tab label="Identified Variants" id="tab-1" aria-controls="tabpanel-1" className={classes.tab} />
            </Tabs>
            <TabPanel value={selectedTab} index={0} keepMounted>
              {patientData.length > 0 ? (
                <PatientTable
                  data={patientData}
                  programId={programId}
                  breadCrumbs={breadCrumbs}
                  viewScope={VIEW_SCOPE.programSummary}
                />
              ) : (
                <div className="noData">No Patient Data</div>
              )}
            </TabPanel>
            <TabPanel value={selectedTab} index={1} preload>
              {variantData.length > 0 ? (
                <VariantsTable
                  data={variantData}
                  programId={programId}
                  breadCrumbs={breadCrumbs}
                  sortTableByDateDesc={sortTableByDateDesc}
                />
              ) : (
                <div className="noData">No Variant Data</div>
              )}
            </TabPanel>
          </div>
        </div>
      )}
      <RunWorkflowModal
        closeModal={closeRunWorkflowModal}
        modalIsOpen={runWorkflowModalIsOpen}
        programId={sessionStorage.getItem('program')}
      />
    </div>
  );
};

export default ProgramSummary;
